import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import LuminateDiscoveryFilters, {
  DEFAULT_LUMINATE_DISCOVERY_FILTERS,
} from '../components/snoop/LuminateDiscoveryFilters'
import { fetchLuminateSummary, fetchLuminateTrackHistory, updateLuminateStatus } from '../api/luminateClient'
import {
  LUMINATE_EXCLUDED_STATUSES,
  LUMINATE_STATUS_OPTIONS,
  luminateRowsToRemovePredicate,
  luminateStatusPolarity,
  luminateStoredStatus,
} from '../utils/snoop/statusOptions'
import { PLACEHOLDER_LUMINATE_TRACKS } from '../utils/snoop/placeholderColumns'

const LUMINATE_TRACK_OUTREACH_CONFIG = {
  artistColumn: 'Display Artist',
  songTitleColumns: ['Track Title'],
  confirmMultiArtistName: true,
}

const LUMINATE_TRUNCATE_COLUMNS = [
  'Track Title',
  'Display Artist',
  'Imprint',
  'Luminate Genres',
  'Luminate Song ID',
]

const LUMINATE_NEC_COLUMNS = ['Track Title', 'Display Artist']

const TIER_RANK = { track: 3, artist: 2, label: 1 }

const TIER_META = {
  track: {
    label: 'Track',
    statusCol: 'Track Status',
    // UI option sent to API for tier routing; sheet/DB store bare status.
    companion: {
      Signed: 'Signed Track',
      Unsigned: 'Unsigned Track',
      Junk: 'Junk Track',
      'Likely Junk': 'Likely Junk Track',
    },
  },
  artist: {
    label: 'Artist',
    statusCol: 'Primary Artist Status',
    companion: {
      Signed: 'Signed Artist',
      Unsigned: 'Unsigned Artist',
      Junk: 'Junk Artist',
      'Likely Junk': 'Likely Junk Artist',
    },
  },
  label: {
    label: 'Label',
    statusCol: 'Imprint Status',
    companion: {
      Signed: 'Signed Label',
      Unsigned: 'Unsigned Label',
      Junk: 'Junk Label',
      'Likely Junk': 'Likely Junk Label',
    },
  },
}

function parseStatusTier(status) {
  const s = String(status || '').trim()
  if (!s) return null
  const m = /^(Signed|Unsigned|Junk|Likely Junk)\s+(Label|Artist|Track)$/i.exec(s)
  if (m) {
    const polarityRaw = m[1]
    const polarity = polarityRaw.toLowerCase().startsWith('likely')
      ? 'Likely Junk'
      : polarityRaw[0].toUpperCase() + polarityRaw.slice(1).toLowerCase()
    return {
      polarity,
      tier: m[2].toLowerCase(),
    }
  }
  // Sheet-canonical bare polarity / junk
  const lower = s.toLowerCase()
  if (lower === 'likely junk') return { polarity: 'Likely Junk', tier: 'artist' }
  if (lower === 'junk') return { polarity: 'Junk', tier: 'artist' }
  if (lower === 'unsigned') return { polarity: 'Unsigned', tier: 'artist' }
  if (lower === 'signed') return { polarity: 'Signed', tier: 'artist' }
  return { polarity: 'Signed', tier: 'artist' }
}

/** Higher-tier statuses that would still win over the new selection. */
function relatedHigherTier(nextStatus, row) {
  const parsed = parseStatusTier(nextStatus)
  if (!parsed) return []

  const nextRank = TIER_RANK[parsed.tier] || 0
  const out = []
  for (const tier of ['track', 'artist']) {
    if (TIER_RANK[tier] <= nextRank) continue
    const meta = TIER_META[tier]
    const current = String(row?.[meta.statusCol] || '').trim()
    if (!current) continue
    const companion = meta.companion[parsed.polarity]
    if (!companion) continue
    // Sheet may store bare Signed/Unsigned/Junk — treat matching family as already aligned
    const curLower = current.toLowerCase()
    const alreadyAligned =
      parsed.polarity === 'Junk' || parsed.polarity === 'Likely Junk'
        ? curLower.startsWith('junk') || curLower.startsWith('likely junk')
        : luminateStatusPolarity(current) === parsed.polarity
    if (alreadyAligned) continue
    out.push({
      tier,
      tierLabel: meta.label,
      current,
      companion,
      companionStored: luminateStoredStatus(companion),
    })
  }
  return out
}

function StatusRelatedPrompt({ prompt, onConfirm, onSkip, onCancel }) {
  if (!prompt) return null
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
      >
        <h2 className="text-base font-semibold text-gray-900">Also update related statuses?</h2>
        <p className="mt-2 text-sm text-gray-600">
          Setting <span className="font-medium text-gray-900">{prompt.nextStatus}</span> leaves
          higher-tier statuses in place (track overrides artist overrides label). Confirm which
          layers should change:
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-gray-700">
          {prompt.related.map((item) => (
            <li key={item.tier} className="rounded-lg bg-gray-50 px-3 py-2">
              <span className="font-medium">{item.tierLabel}</span>: {item.current}
              <span className="text-gray-500"> → </span>
              <span className="font-medium text-emerald-800">
                {item.companionStored || item.companion}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-gray-600">
          Update {prompt.related.map((r) => r.tierLabel.toLowerCase()).join(' + ')} to match, or keep
          only the selection you just made.
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3.5 py-2 text-sm font-medium text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Only {prompt.nextStatus}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-gray-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Also update related
          </button>
        </div>
      </div>
    </div>
  )
}

export default function LuminateSummaryPage() {
  const [discoveryFilters, setDiscoveryFilters] = useState(DEFAULT_LUMINATE_DISCOVERY_FILTERS)
  const [hideNonEnglish, setHideNonEnglish] = useState(true)
  const fetchData = useCallback(
    () => fetchLuminateSummary(discoveryFilters),
    [discoveryFilters],
  )
  const [prompt, setPrompt] = useState(null)

  const askRelated = useCallback((nextStatus, row) => {
    const related = relatedHigherTier(nextStatus, row)
    if (!related.length) return Promise.resolve([])
    return new Promise((resolve, reject) => {
      setPrompt({
        nextStatus,
        related,
        resolve: (alsoUpdate) => {
          setPrompt(null)
          resolve(alsoUpdate)
        },
        reject: () => {
          setPrompt(null)
          reject(new Error('Status change cancelled'))
        },
      })
    })
  }, [])

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'luminate',
        uidColumn: 'Luminate Song ID',
        options: LUMINATE_STATUS_OPTIONS,
        removeAfterSave: true,
        shouldRemoveRow: (status, { alsoUpdate } = {}) => {
          const all = [status, ...(alsoUpdate || [])].filter(Boolean)
          return all.some(
            (s) =>
              LUMINATE_EXCLUDED_STATUSES.has(String(s)) ||
              luminateStatusPolarity(s) === 'Signed',
          )
        },
        rowsToRemove: (status, ctx) => luminateRowsToRemovePredicate(status, ctx),
        prepareSave: async ({ status, row }) => {
          const alsoUpdate = await askRelated(status, row)
          return { alsoUpdate }
        },
        onSave: async ({ status, row, alsoUpdate }) => {
          await updateLuminateStatus({
            status,
            songLuminateId: row?.['Luminate Song ID'],
            primaryArtist: row?.['Primary Artist'],
            imprint: row?.Imprint,
            trackTitle: row?.['Track Title'],
            displayArtist: row?.['Display Artist'],
            alsoUpdate: alsoUpdate || [],
          })
          return { alsoUpdate: alsoUpdate || [] }
        },
      },
    }),
    [askRelated],
  )

  return (
    <>
      <SummaryDashboard
        title="Luminate Tracks"
        subtitle="Track weekly · streams / % chg / residual"
        fetchData={fetchData}
        defaultSort={[
          { column: 'Residual', direction: 'desc' },
        ]}
        searchPlaceholder="Search title, artist, status…"
        layout="unsigned"
        truncateColumns={LUMINATE_TRUNCATE_COLUMNS}
        hiddenColumns={[
          'Primary Artist',
          'Main Artist IDs',
          'Track Status',
          'Primary Artist Status',
          'Imprint Status',
        ]}
        placeholderColumns={PLACEHOLDER_LUMINATE_TRACKS}
        editableStatus={editableStatus}
        enableOutreachColumn
        outreachConfig={LUMINATE_TRACK_OUTREACH_CONFIG}
        enableHideNonEnglish
        hideNonEnglish={hideNonEnglish}
        onHideNonEnglishChange={setHideNonEnglish}
        showHideNonEnglishToggle={false}
        hideNonEnglishColumns={LUMINATE_NEC_COLUMNS}
        enableGenreStartsWithFilter
        expandableHistory
        historyFetcher={async (row) => {
          const songId = row?.['Luminate Song ID']
          if (!songId) return { series: [] }
          return fetchLuminateTrackHistory(songId)
        }}
        extraActions={
          <LuminateDiscoveryFilters
            filters={discoveryFilters}
            onChange={setDiscoveryFilters}
            hideNonEnglish={hideNonEnglish}
            onHideNonEnglishChange={setHideNonEnglish}
          />
        }
      />
      <StatusRelatedPrompt
        prompt={prompt}
        onConfirm={() => prompt?.resolve(prompt.related.map((r) => r.companion))}
        onSkip={() => prompt?.resolve([])}
        onCancel={() => prompt?.reject()}
      />
    </>
  )
}
